import { ITextBuilder } from "../../../../Global/Base/Builders/ITextBuilder";
import { PromiseUtils } from "../../../../Global/Base/Promises/PromiseUtils";
import { DateUtils } from "../../../../Global/Base/Utils/DateUtils";
import { IValidatable, IValidationResult } from "../../../UI.BaseControls/ControlInterfaces";
import { DateEdit } from "../../../UI.BaseControls/DateEdit";
import { TimeEdit } from "../../../UI.BaseControls/TimeEdit";
import { ControlParams } from "./ControlInterfaces";
import { EditableControl } from "./EditableControl";

export class DateTimeEdit extends EditableControl {
    private _timeEdit: TimeEdit;
    private _dateEdit: DateEdit;
    private _originalValue: Date;
    public override isCompositeComponent: boolean = true;

    protected _placeholderTimeLocKey: string;
    protected _placeholderDateLocKey: string;

    public override set controlValue(value: Date) {
        this._originalValue = value;

        this._dateEdit.value = value;
        this._timeEdit.value = value;
    }

    public override get controlValue(): Date {
        return this._originalValue;
    }

    public override get displayedValue(): ITextBuilder {
        // tady je to dost komplikovany pripad
        // - pokud bude DTE v bunce seznamu a uzivatel preklikne na jiny radek,
        // tak se tato hodnota prenese do needitacniho pole a hodnota musi uzivateli stale davat smysl
        // - ve stejne situaci na datailu musi byt mozne sem (pres tuto property) zpetne vlozit nevalidni hodnotu a vlozit jeji jednotlive casti do jednotlivych komponent

        return {
            dateEdit: this._dateEdit.displayedValue,
            timeEdit: this._timeEdit.displayedValue,
            toString: () => {
                return `${this._dateEdit.displayedValue} ${this._timeEdit.displayedValue}`; // `
            },
        } as ITextBuilder;
    }

    public override set displayedValue(v: ITextBuilder) {
        if (!v)
            return;

        const data = v as { dateEdit: string; timeEdit: string };

        this._dateEdit.displayedValue = data.dateEdit;
        this._timeEdit.displayedValue = data.timeEdit;
    }

    public override set enabled(val: boolean) {
        if (this._enabled !== val) {
            this._enabled = val;
            this._dateEdit.enabled = val;
            this._timeEdit.enabled = val;
            this.element.classList.toggle("Enabled", val);
            this.element.classList.toggle("Disabled", !val);
            this.enabledChanged.trigger(this, { value: val });
        }
    }

    public override get enabled(): boolean {
        return this._enabled;
    }

    public override get enabledVisual(): boolean {
        return super.enabledVisual;
    }

    public override set enabledVisual(value: boolean) {
        super.enabledVisual = value;
        // Změnu vlastnosti enabledVisual je potřeba propagovat na vnitřní komponenty DateEdit a TimeEdit. Na vnitřních komponentách kompozitních komponent (this.isCompositeComponent) se totiž nevyhodnocují práva.
        this._dateEdit.enabled = value;
        this._timeEdit.enabled = value;
    }

    public override set isEditing(val: boolean) {
        if (this._isEditing !== val) {
            this._isEditing = val;
            this._dateEdit.isEditing = val;
            this._timeEdit.isEditing = val;
            this.isEditingChanged.trigger(this, { value: val });
        }
    }

    public override get isEditing(): boolean {
        return this._isEditing;
    }

    constructor(params: ControlParams) {
        super(params);
        const { element } = params;
        element.classList.add("DateTimeEdit");
        this.attrName = this.getDataAttr("attrname");
        this._placeholderDateLocKey = element.getAttribute("data-placeholderdatelc");
        this._placeholderTimeLocKey = element.getAttribute("data-placeholdertimelc");
        this._originalValue = DateUtils.fromXsdDateTime("0100-01-01T00:00:00");

        this._createElement();
        this._setListener();
    }

    private _createElement(): void {
        this.innerElement = document.createElement("div");
        this.element.appendChild(this.innerElement);
        this.innerElement.classList.add("DateTimeEditParent");

        if (this._errorElement)
            this._errorElement.classList.add("ErrorElement");
        //this.ErrorElement.style.display = "none";

        this._dateEdit = new DateEdit();
        this._dateEdit.setInnerElementID(this.element.getAttribute("data-componentid"));
        this._dateEdit.placeholderLocKey = this._placeholderDateLocKey;
        this._timeEdit = new TimeEdit(); //new DateEdit(dateEditElement);
        this._timeEdit.placeholderLocKey = this._placeholderTimeLocKey;
        this._dateEdit.enabled = this.enabled;
        this._timeEdit.enabled = this.enabled;
        this._dateEdit.isEditing = this.isEditing;
        this._timeEdit.isEditing = this.isEditing;
        if (this.element.hasAttribute("data-timeformat"))
            this._timeEdit.setAttribute("data-timeformat", this.element.getAttribute("data-timeformat"));

        this._dateEdit.errorTooltipDisabled = true;
        this._timeEdit.errorTooltipDisabled = true;

        const tformat = this.getDataAttr("timeFormat");
        if (tformat)
            this._timeEdit.setAttribute("timeFormat", tformat);
        this.innerElement.appendChild(this._dateEdit);
        this.innerElement.appendChild(this._timeEdit);
    }

    private _setListener(): void {
        this.element.addEventListener("focusout", () => {
            if (!this.isValid) // stav nevalidnosti nektere komponenty se nebude prepisovat stavem, ze jedno pole neni vyplneno
                return;

            if (this._dateEdit.controlValue && !this._timeEdit.controlValue || !this._dateEdit.controlValue && this._timeEdit.controlValue) {
                // je potřeba počkat, až se projeví ztráta focusu
                PromiseUtils.delay(0).then(() => {
                    // pokud ani jedna subkomponenta nemá focus, tak se validuje, jestli jsou obě nevyplněné nebo obě vyplněné
                    if (!this._timeEdit.isFocused && !this._dateEdit.isFocused &&
                        (this._dateEdit.controlValue && !this._timeEdit.controlValue || !this._dateEdit.controlValue && this._timeEdit.controlValue))
                        this.setInvalid("VAL-615396"); // Všechna pole musí být vyplněna
                });
            }
            this.element.classList.remove("focused");
        });

        this.element.addEventListener("focusin", () => {
            if (this.enabled)
                this.element.classList.add("focused");
            this.validate(); // provede se validace, aby se odstranilo pripadna hlaska, ze musi byt vyplnena vsechna pole
        });

        this._timeEdit.afterAttach(() => {
            this._timeEdit.registerEventListener("change", (e) => {
                e.stopPropagation();
                this._valueChanged();
            });

            this._timeEdit.validityChanged.add((_element: IValidatable, validity: IValidationResult) => {
                this._checkValidity(validity);
            });
        });

        this._dateEdit.afterAttach(() => {
            this._dateEdit.registerEventListener("change", (e) => {
                e.stopPropagation();
                this._valueChanged();
            });

            this._dateEdit.validityChanged.add((_element: IValidatable, validity: IValidationResult) => {
                this._checkValidity(validity);
            });
        });
    }

    private _valueChanged(): void {
        if (!this.isValid || !this._isGuiValid)
            return;

        if (this._updateOriginalValue())
            this.valueChanged.trigger(this, null);
    }

    private _updateOriginalValue(): boolean {
        const date = this._dateEdit.controlValue;
        const time = this._timeEdit.controlValue;

        // az kdyz jsou vyplnena obe pole, tak se sklada vysledna hodnota DateTimeEditu
        if (date && time) {
            this._originalValue = new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.getHours(), time.getMinutes(), time.getSeconds(), time.getMilliseconds());
        } else {
            this._originalValue = null;
        }

        // zmena hodnoty se ohlasuje az kdyz jsou obe casti vyplnene nebo obe prazdne
        return (time != null && date != null) || (time == null && date == null);
    }

    private _checkValidity(validity: IValidationResult): void {
        if (!validity.valid)
            this.setInvalid("GUI-55627"); // "Nevalidní hodnota v poli"
        else if (this._dateEdit.isValid && this._timeEdit.isValid)
            this.setValid(); // ale zpátky se může zvalidnit až jsou opět validní obě
    }

    public override validate(): boolean {
        this._dateEdit.validate();
        this._timeEdit.validate();
        return this.isValid;
    }

    /**
     * Zahájí editaci zaměřením pole pro zadávání data.
     *
     * @see IStartEdit
     */
    public override startEdit(): void {
        this._dateEdit.focus();
    }
}
